import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Search,
  Settings,
  ChevronDown,
  ChevronUp,
  Handshake,
  PieChart,
  Scale,
  Plus,
  Utensils,
  Receipt,
  Fuel,
  ShoppingBag,
  Film,
  Sparkles,
  ArrowRightLeft,
  X,
  Share2,
  Calendar
} from 'lucide-react';

/**
 * Returns a category icon and pastel color palette tailored for group feeds.
 */
export function getCategoryBadge(categoryName, description = '') {
  const cat = (categoryName || '').toLowerCase();
  const desc = (description || '').toLowerCase();

  if (cat.includes('food') || cat.includes('dining') || cat.includes('restaurant') || desc.includes('tea') || desc.includes('coffee') || desc.includes('lunch') || desc.includes('dinner')) {
    return {
      icon: Utensils,
      bg: 'rgba(56, 161, 105, 0.15)',
      color: '#68d391',
      border: 'rgba(56, 161, 105, 0.3)'
    };
  }
  if (cat.includes('fuel') || cat.includes('gas') || cat.includes('petrol') || desc.includes('fuel') || desc.includes('gas')) {
    return {
      icon: Fuel,
      bg: 'rgba(254, 178, 178, 0.15)',
      color: '#feb2b2',
      border: 'rgba(254, 178, 178, 0.3)'
    };
  }
  if (cat.includes('movie') || cat.includes('entertainment') || cat.includes('cinema') || desc.includes('movie')) {
    return {
      icon: Film,
      bg: 'rgba(183, 148, 244, 0.15)',
      color: '#d6bcfa',
      border: 'rgba(183, 148, 244, 0.3)'
    };
  }
  if (cat.includes('grocer') || cat.includes('supermarket') || desc.includes('grocery')) {
    return {
      icon: ShoppingBag,
      bg: 'rgba(72, 187, 120, 0.15)',
      color: '#9ae6b4',
      border: 'rgba(72, 187, 120, 0.3)'
    };
  }
  if (cat.includes('opening') || desc.includes('opening balance')) {
    return {
      icon: Scale,
      bg: 'rgba(0, 245, 212, 0.15)',
      color: '#00f5d4',
      border: 'rgba(0, 245, 212, 0.3)'
    };
  }
  if (cat.includes('deposit') || cat.includes('settle') || desc.includes('settle') || desc.includes('repayment')) {
    return {
      icon: ArrowRightLeft,
      bg: 'rgba(0, 245, 212, 0.15)',
      color: '#00f5d4',
      border: 'rgba(0, 245, 212, 0.3)'
    };
  }

  // Default General / Receipt
  return {
    icon: Receipt,
    bg: 'rgba(160, 174, 192, 0.15)',
    color: '#cbd5e0',
    border: 'rgba(160, 174, 192, 0.3)'
  };
}

/**
 * Formats date into Month / Day display tokens.
 */
export function getDateTokens(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  return {
    monthShort: monthNames[d.getMonth()],
    monthLong: fullMonthNames[d.getMonth()],
    day: d.getDate(),
    year: d.getFullYear(),
    monthYearKey: `${fullMonthNames[d.getMonth()]} ${d.getFullYear()}`
  };
}

export default function GroupDetailHub({
  activeGroup,
  groupMembers = [],
  transactions = [],
  balances = {},
  transfers = [],
  repaymentsSaved = 0,
  currentUserId,
  formatMoney,
  isBalancesLoading = false,
  onBack,
  onOpenSettings,
  onOpenBalances,
  onOpenCharts,
  onOpenSettleUp,
  onAddExpense,
  onSelectTx
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Group transactions for this specific group
  const groupTxs = useMemo(() => {
    return transactions
      .filter(tx => tx.group_id === activeGroup?.group_id)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transactions, activeGroup]);

  // Filtered transactions by search query
  const filteredTxs = useMemo(() => {
    if (!searchQuery.trim()) return groupTxs;
    const q = searchQuery.toLowerCase();
    return groupTxs.filter(tx => {
      const desc = (tx.description || '').toLowerCase();
      const cat = (tx.category_name || '').toLowerCase();
      const amt = String(tx.total_amount || '');
      return desc.includes(q) || cat.includes(q) || amt.includes(q);
    });
  }, [groupTxs, searchQuery]);

  // Group filtered transactions by Month Year (e.g. "July 2026", "June 2026")
  const groupedByMonth = useMemo(() => {
    const map = new Map();
    filteredTxs.forEach(tx => {
      const { monthYearKey } = getDateTokens(tx.date);
      if (!map.has(monthYearKey)) {
        map.set(monthYearKey, []);
      }
      map.get(monthYearKey).push(tx);
    });
    return Array.from(map.entries());
  }, [filteredTxs]);

  // Determine current user's net standing in this group
  const userNetBalance = balances[currentUserId] || 0;
  const isUserCreditor = userNetBalance > 0.005;
  const isUserDebtor = userNetBalance < -0.005;

  // Breakdown of who owes user or who user owes
  const userSubDebts = useMemo(() => {
    const res = [];
    (transfers || []).forEach(t => {
      if (t.to_user_id === currentUserId) {
        // Someone owes current user
        res.push({
          otherUserId: t.from_user_id,
          otherName: t.from_name || 'Group member',
          amount: t.amount,
          type: 'owes_user'
        });
      } else if (t.from_user_id === currentUserId) {
        // Current user owes someone
        res.push({
          otherUserId: t.to_user_id,
          otherName: t.to_name || 'Group member',
          amount: t.amount,
          type: 'user_owes'
        });
      }
    });
    return res;
  }, [transfers, currentUserId]);

  const currency = activeGroup?.currency || 'DH';

  // Helper to compute user balance impact for a specific transaction
  const getUserTxImpact = (tx) => {
    const isSinglePayer = !tx.splits || !tx.splits.some(s => parseFloat(s.paid_amount || 0) > 0);
    const payerId = tx.user_id || tx.paid_by_user_id;
    const userSplit = tx.splits?.find(s => s.member_user_id === currentUserId);
    const userOwed = parseFloat(userSplit?.owed_amount || 0);

    if (isSinglePayer) {
      if (payerId === currentUserId) {
        const total = parseFloat(tx.total_amount || 0);
        const lent = total - userOwed;
        if (lent > 0.005) {
          return { type: 'lent', label: 'you lent', amount: lent };
        }
        return { type: 'self', label: 'you paid for yourself', amount: total };
      } else if (userOwed > 0.005) {
        return { type: 'borrowed', label: 'you borrowed', amount: userOwed };
      }
      return { type: 'neutral', label: 'not involved', amount: 0 };
    } else {
      const userPaid = parseFloat(userSplit?.paid_amount || 0);
      const net = userPaid - userOwed;
      if (net > 0.005) {
        return { type: 'lent', label: 'you lent', amount: net };
      } else if (net < -0.005) {
        return { type: 'borrowed', label: 'you borrowed', amount: -net };
      }
      return { type: 'neutral', label: 'not involved', amount: 0 };
    }
  };

  // Helper to get payer label for transaction subtitle
  const getTxSubtitle = (tx) => {
    const payerId = tx.paid_by_user_id || tx.user_id;
    const payerMember = groupMembers.find(m => m.user_id === payerId);
    const payerName = payerId === currentUserId 
      ? 'You' 
      : (payerMember?.display_name || payerMember?.nickname || payerMember?.email?.split('@')[0] || 'Someone');
    
    return `${payerName} paid ${formatMoney(tx.total_amount, tx.currency || currency)}`;
  };

  return (
    <div className="group-detail-hub animate-fade-in" style={{ paddingBottom: '80px' }}>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        background: 'rgba(18, 24, 38, 0.85)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border-glass)',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--border-glass)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-main)',
              cursor: 'pointer'
            }}
            title="Back to Groups"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', letterSpacing: '-0.3px' }}>
              {activeGroup?.group_name || 'Group'}
            </h2>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {groupMembers.length} member{groupMembers.length !== 1 ? 's' : ''} • {activeGroup?.type || 'Group'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setIsSearching(!isSearching)}
            style={{
              background: isSearching ? 'rgba(0, 245, 212, 0.15)' : 'rgba(255, 255, 255, 0.06)',
              border: isSearching ? '1px solid var(--accent-cyan)' : '1px solid var(--border-glass)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isSearching ? 'var(--accent-cyan)' : 'var(--text-main)',
              cursor: 'pointer'
            }}
            title="Search group expenses"
          >
            <Search size={17} />
          </button>
          <button
            type="button"
            onClick={onOpenSettings}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--border-glass)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-main)',
              cursor: 'pointer'
            }}
            title="Group settings & members"
          >
            <Settings size={17} />
          </button>
        </div>
      </div>

      {/* Instant Search Bar */}
      {isSearching && (
        <div className="animate-fade-in" style={{ padding: '12px 20px', background: 'rgba(15, 20, 32, 0.95)', borderBottom: '1px solid var(--border-glass)' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '11px', color: 'var(--text-muted)' }} />
            <input
              type="text"
              autoFocus
              placeholder="Search expenses in this group..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 36px 9px 36px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ position: 'absolute', right: '10px', top: '10px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Container */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        {/* Overall Net Balance Summary Card (Collapsible Accordion) */}
        <div className="glass-panel" style={{
          padding: '16px',
          borderRadius: '16px',
          border: '1px solid var(--border-glass)',
          background: 'linear-gradient(145deg, rgba(26, 32, 48, 0.7) 0%, rgba(18, 22, 34, 0.9) 100%)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)'
        }}>
          <div
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                {isBalancesLoading && Object.keys(balances).length === 0 ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)' }}>
                    <span className="animate-spin" style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', border: '2px solid var(--accent-cyan)', borderTopColor: 'transparent' }} />
                    Calculating group balances...
                  </span>
                ) : isUserCreditor ? (
                  <>You are owed <span style={{ color: 'var(--color-inflow)', fontWeight: '800' }}>{formatMoney(userNetBalance, currency)}</span> overall</>
                ) : isUserDebtor ? (
                  <>You owe <span style={{ color: 'var(--color-outflow)', fontWeight: '800' }}>{formatMoney(Math.abs(userNetBalance), currency)}</span> overall</>
                ) : (
                  <>You are all settled up overall</>
                )}
              </span>
            </div>
            <button
              type="button"
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', padding: '4px', cursor: 'pointer' }}
            >
              {summaryExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          {/* Expanded Sub-debts */}
          {summaryExpanded && (
            <div className="animate-fade-in" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
              {userSubDebts.length === 0 ? (
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>No individual debts pending for you in this group.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderLeft: '3px solid var(--accent-cyan)', paddingLeft: '12px' }}>
                  {userSubDebts.map((item, idx) => (
                    <div key={idx} style={{ fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {item.type === 'owes_user' ? (
                          <><strong style={{ color: 'var(--text-main)' }}>{item.otherName}</strong> owes you <strong style={{ color: 'var(--color-inflow)' }}>{formatMoney(item.amount, currency)}</strong></>
                        ) : (
                          <>You owe <strong style={{ color: 'var(--text-main)' }}>{item.otherName}</strong> <strong style={{ color: 'var(--color-outflow)' }}>{formatMoney(item.amount, currency)}</strong></>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Button Row (Pills) */}
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          overflowX: 'auto',
          paddingBottom: '4px',
          scrollbarWidth: 'none'
        }}>
          <button
            type="button"
            onClick={onOpenBalances}
            style={{
              padding: '8px 16px',
              borderRadius: '24px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'rgba(255, 255, 255, 0.07)',
              color: 'var(--text-main)',
              border: '1px solid var(--border-glass)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            <Scale size={15} color="#00f5d4" /> Balances
          </button>

          <button
            type="button"
            onClick={onAddExpense}
            style={{
              padding: '8px 18px',
              borderRadius: '24px',
              fontSize: '13px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, rgba(0, 245, 212, 0.22) 0%, rgba(0, 245, 212, 0.08) 100%)',
              color: 'var(--accent-cyan)',
              border: '1px solid rgba(0, 245, 212, 0.5)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(0, 245, 212, 0.15)'
            }}
          >
            <Plus size={16} /> Add Expense
          </button>

          <button
            type="button"
            onClick={onOpenSettleUp}
            style={{
              padding: '8px 18px',
              borderRadius: '24px',
              fontSize: '13px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #ff7a18 0%, #ff5238 100%)',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(255, 82, 56, 0.3)'
            }}
          >
            <Handshake size={15} /> Settle up
          </button>

          <button
            type="button"
            onClick={onOpenCharts}
            style={{
              padding: '8px 16px',
              borderRadius: '24px',
              fontSize: '13px',
              fontWeight: '600',
              background: 'rgba(255, 255, 255, 0.07)',
              color: 'var(--text-main)',
              border: '1px solid var(--border-glass)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            <PieChart size={15} color="#b388ff" /> Charts
          </button>
        </div>

        {/* Group Expense Feed Grouped By Month */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '4px' }}>
          {groupedByMonth.length === 0 ? (
            <div className="glass-panel" style={{ padding: '32px 20px', textAlign: 'center', borderRadius: '16px' }}>
              <Receipt size={36} color="var(--text-muted)" style={{ margin: '0 auto 12px auto', display: 'block', opacity: 0.6 }} />
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                {searchQuery ? 'No matching expenses found' : 'No expenses recorded yet'}
              </p>
              <p style={{ margin: '6px 0 16px 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                {searchQuery ? 'Try another search term' : 'Add your first group expense to split costs with members'}
              </p>
              {!searchQuery && (
                <button
                  type="button"
                  onClick={onAddExpense}
                  className="btn-primary"
                  style={{ padding: '8px 18px', borderRadius: '12px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <Plus size={15} /> Add First Expense
                </button>
              )}
            </div>
          ) : (
            groupedByMonth.map(([monthYear, txList]) => (
              <div key={monthYear} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h4 style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  margin: '0 0 4px 4px'
                }}>
                  {monthYear}
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {txList.map(tx => {
                    const badge = getCategoryBadge(tx.category_name, tx.description);
                    const IconComp = badge.icon;
                    const { monthShort, day } = getDateTokens(tx.date);
                    const impact = getUserTxImpact(tx);

                    return (
                      <div
                        key={tx.transaction_id}
                        onClick={() => onSelectTx && onSelectTx(tx)}
                        className="glass-panel"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 14px',
                          borderRadius: '14px',
                          border: '1px solid var(--border-glass)',
                          cursor: 'pointer',
                          gap: '12px',
                          transition: 'transform 0.15s ease, background 0.15s ease'
                        }}
                      >
                        {/* Left: Date + Icon */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                          {/* Date Block */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '34px',
                            flexShrink: 0
                          }}>
                            <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600', lineHeight: 1 }}>
                              {monthShort}
                            </span>
                            <span style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-main)', lineHeight: 1.2 }}>
                              {day}
                            </span>
                          </div>

                          {/* Category Square Badge */}
                          <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '12px',
                            background: badge.bg,
                            border: `1px solid ${badge.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <IconComp size={20} color={badge.color} />
                          </div>

                          {/* Description & Payer info */}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{
                              fontWeight: '700',
                              fontSize: '14px',
                              color: 'var(--text-main)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {tx.description || 'Expense'}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: 'var(--text-muted)',
                              marginTop: '2px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {getTxSubtitle(tx)}
                            </div>
                          </div>
                        </div>

                        {/* Right: User Balance Impact */}
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          {impact.type === 'lent' ? (
                            <>
                              <span style={{ fontSize: '10px', color: '#68d391', display: 'block', textTransform: 'lowercase', fontWeight: '500' }}>
                                you lent
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--color-inflow)' }}>
                                {formatMoney(impact.amount, tx.currency || currency)}
                              </span>
                            </>
                          ) : impact.type === 'borrowed' ? (
                            <>
                              <span style={{ fontSize: '10px', color: '#feb2b2', display: 'block', textTransform: 'lowercase', fontWeight: '500' }}>
                                you borrowed
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--color-outflow)' }}>
                                {formatMoney(impact.amount, tx.currency || currency)}
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block' }}>
                                {impact.label}
                              </span>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)' }}>
                                {formatMoney(tx.total_amount, tx.currency || currency)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
